import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { LienConsole, Montant, NonComptees, Section } from './composants.js';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import styles from './Partage.module.css';

/**
 * Le partage entre associés : pour chaque ligne, sa part et ses versements
 * mois par mois. L'API ne renvoie à l'associé que sa propre ligne ; la
 * direction voit toutes les lignes. Un « dû » à saisir n'est dans aucun total.
 */
export function Partage({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);

  return (
    <Section
      id="partage"
      numero={numero}
      titre={t('vueDAigle.partage.titre')}
      note={
        vue.vue === 'associe'
          ? t('vueDAigle.partage.noteAssocie')
          : t('vueDAigle.partage.noteDirection')
      }
      actions={
        peut('console:manage-associates') ? (
          <LienConsole vers="/associes" bouton>
            {t('vueDAigle.partage.gerer')}
          </LienConsole>
        ) : undefined
      }
    >
      <div className={styles.lignes}>
        {vue.partage.map((ligne, rang) => {
          const totalDu = ligne.versements.reduce((s, v) => s + (v.montantDuCentimes ?? 0), 0);
          const totalVerse = ligne.versements.reduce((s, v) => s + v.verseCentimes, 0);
          const aSaisir = ligne.versements.filter((v) => v.montantDuCentimes === null).length;
          // Aucun dû saisi : le total est à saisir, pas 0 €.
          const toutASaisir = aSaisir === ligne.versements.length;
          return (
            <article key={`${rang}-${ligne.libelle}`} className={styles.ligne}>
              <header className={styles.tete}>
                <h3>{ligne.libelle}</h3>
                <span className={styles.part}>
                  {ligne.partPointsDeBase === null ? (
                    <span className={commun.aSaisir}>{t('vueDAigle.partage.partAbsente')}</span>
                  ) : (
                    t('vueDAigle.partage.part', {
                      pourcent: f.pourcentDePoints(ligne.partPointsDeBase),
                    })
                  )}
                </span>
              </header>
              <div className={commun.tableauConteneur}>
                <table className={`${commun.table} ${styles.table}`}>
                  <caption className={commun.srOnly}>
                    {t('vueDAigle.partage.caption', { libelle: ligne.libelle })}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('vueDAigle.partage.colMois')}</th>
                      <th scope="col" className={commun.num}>
                        {t('vueDAigle.partage.colDu')}
                      </th>
                      <th scope="col" className={commun.num}>
                        {t('vueDAigle.partage.colVerse')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ligne.versements.map((versement) => (
                      <tr key={versement.mois}>
                        <th scope="row">{f.moisAnnee(versement.mois)}</th>
                        <td className={commun.num}>
                          <Montant centimes={versement.montantDuCentimes} />
                        </td>
                        <td className={commun.num}>{f.euros(versement.verseCentimes)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">{t('vueDAigle.partage.total')}</th>
                      <td className={commun.num}>
                        {toutASaisir ? (
                          <span className={commun.aSaisir}>{t('vueDAigle.aSaisir')}</span>
                        ) : (
                          f.euros(totalDu)
                        )}
                      </td>
                      <td className={commun.num}>{f.euros(totalVerse)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <NonComptees nombre={aSaisir} />
            </article>
          );
        })}
      </div>
    </Section>
  );
}
